import { Link } from "react-router-dom";
import { TopBar } from "../components/Layout";

const WORKERS = [
  { name: "ac-edge-staging", url: "https://ac-edge-staging.wpolanco.workers.dev", role: "Host router + tenant resolution + apex/tenant API" },
  { name: "ac-admin-staging", url: "https://ac-admin-staging.wpolanco.workers.dev", role: "Control-plane API (this console)" },
  { name: "ac-portal-staging", url: "https://ac-portal-staging.wpolanco.workers.dev", role: "Customer portal API" },
  { name: "ac-provisioner-staging", url: "https://ac-provisioner-staging.wpolanco.workers.dev", role: "Provisioning queue consumer" },
  { name: "ac-email-in-staging", url: "https://ac-email-in-staging.wpolanco.workers.dev", role: "Inbound email (Email Routing)" },
  { name: "ac-email-out-staging", url: "https://ac-email-out-staging.wpolanco.workers.dev", role: "Outbound email queue consumer" },
  { name: "ac-notifications-staging", url: "https://ac-notifications-staging.wpolanco.workers.dev", role: "Notification fan-out queue consumer" },
  { name: "ac-webhooks-staging", url: "https://ac-webhooks-staging.wpolanco.workers.dev", role: "Outbound webhooks queue consumer" },
  { name: "ac-cron-staging", url: "https://ac-cron-staging.wpolanco.workers.dev", role: "Scheduled maintenance (SLA sweep, cleanup, digests)" },
];

// POC tenant — provisioned 2026-06-07 on the staging control-plane.
// `primaryHost` is the tenant's PROD identity (the custom domain it will own once
// actionconnect.com routes/DNS are live). On staging we don't use it as a URL —
// the app is served on its own *.pages.dev host and reaches the tenant API on the
// *.workers.dev edge, tagging requests with X-AC-Tenant-Host so the shared edge
// resolves this tenant. Those dev access URLs are below.
const POC = {
  name: "POC — Proof of Concept",
  tenantId: "01KTJACHJ84NSB9MYQW4HSEPMJ",
  slug: "poc",
  status: "active",
  plan: "Team",
  primaryHost: "poc.actionconnect.com", // prod custom-domain identity
  inboundEmail: "poc@in.actionconnect.com",
  d1Name: "ac-tenant-01ktjachj84nsb9myqw4hsepmj",
  d1Id: "1f9b6017-df76-44f8-b1ff-f94cb142be55",
  adminEmail: "admin@poc.actionconnect.com",
  adminPassword: "PoC-P0kYDDpS-26",
  // Dev access (what actually works today on staging):
  appUrl: "https://ac-app-web-staging.pages.dev",
  edgeUrl: "https://ac-edge-staging.wpolanco.workers.dev",
};

function Copy({ text }: { text: string }) {
  return (
    <button
      className="btn small ghost"
      title="Copy"
      onClick={() => navigator.clipboard?.writeText(text)}
      style={{ padding: "2px 7px" }}
    >
      ⧉
    </button>
  );
}

export function ResourceStagingPage() {
  return (
    <>
      <TopBar crumbs={<><Link to="/resources">Resources</Link> / Staging &amp; POC tenant</>} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Staging &amp; POC tenant</h1>
            <div className="sub">
              The deployed staging environment (Cloudflare <code>*.workers.dev</code>, no production routes) and
              the proof-of-concept tenant&apos;s URLs &amp; credentials.
            </div>
          </div>
        </div>

        <div className="alert error">
          <strong>Staff-only — contains live credentials.</strong> The admin sign-in token and POC admin password
          below are real. Do not share outside the team.
        </div>

        {/* ---- Admin console access ---- */}
        <div className="card">
          <div className="card-head">
            <h2>Admin console (this app)</h2>
            <span className="badge status-active">live</span>
          </div>
          <div className="card-body">
            <dl className="kv">
              <dt>Console URL</dt>
              <dd>
                <a href="https://ac-admin-web-staging.pages.dev" target="_blank" rel="noreferrer" className="mono">
                  ac-admin-web-staging.pages.dev
                </a>{" "}
                <Copy text="https://ac-admin-web-staging.pages.dev" />
              </dd>
              <dt>API</dt>
              <dd className="mono">ac-admin-staging.wpolanco.workers.dev</dd>
              <dt>Sign-in token</dt>
              <dd className="mono">
                acadm_cad229c616d5a83a7d457bd3f0f27fc6a224{" "}
                <Copy text="acadm_cad229c616d5a83a7d457bd3f0f27fc6a224" />
              </dd>
              <dt>Staff email</dt>
              <dd>any staff email (recorded as the audit actor)</dd>
            </dl>
            <p className="muted" style={{ fontSize: "0.84rem", marginBottom: 0 }}>
              This is the placeholder shared-token gate (ARCHITECTURE §11) until staff SSO lands. The token is the{" "}
              <code>ADMIN_API_TOKEN</code> secret on the ac-admin worker.
            </p>
          </div>
        </div>

        {/* ---- Staging worker URLs ---- */}
        <div className="card">
          <div className="card-head">
            <h2>Staging deployment — Workers</h2>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              account wpolanco@zentrosoft.com · 55157df5…
            </span>
          </div>
          <div className="card-body tight">
            <table className="data">
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>URL</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {WORKERS.map((w) => (
                  <tr key={w.name}>
                    <td className="mono">{w.name}</td>
                    <td>
                      <a href={w.url} target="_blank" rel="noreferrer" className="mono">
                        {w.url.replace("https://", "")}
                      </a>{" "}
                      <Copy text={w.url} />
                    </td>
                    <td style={{ fontSize: "0.84rem" }}>{w.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---- Shared staging resources ---- */}
        <div className="card">
          <div className="card-head">
            <h2>Shared staging resources</h2>
          </div>
          <div className="card-body">
            <dl className="kv">
              <dt>Control-plane D1</dt>
              <dd className="mono">ac-control-plane-staging · ef6beaa3-9c55-4cbc-9563-a3b5921cde33</dd>
              <dt>Routing KV</dt>
              <dd className="mono">ac-routing-staging · d35db24c67184111afb0902cefcbd756</dd>
              <dt>R2 buckets</dt>
              <dd className="mono">ac-attachments-staging · ac-raw-email-staging</dd>
              <dt>Queues</dt>
              <dd className="mono">ac-provisioning · ac-email-out · ac-notifications · ac-webhooks (-staging, +dlq each)</dd>
            </dl>
          </div>
        </div>

        {/* ---- POC tenant ---- */}
        <div className="card">
          <div className="card-head">
            <h2>POC tenant</h2>
            <span className="badge status-active">active</span>
          </div>
          <div className="card-body">
            <dl className="kv">
              <dt>Name</dt>
              <dd>{POC.name}</dd>
              <dt>Tenant ID</dt>
              <dd className="mono">{POC.tenantId} <Copy text={POC.tenantId} /></dd>
              <dt>Slug</dt>
              <dd className="mono">{POC.slug}</dd>
              <dt>Plan</dt>
              <dd>{POC.plan}</dd>
              <dt>App URL (dev)</dt>
              <dd>
                <a href={POC.appUrl} target="_blank" rel="noreferrer" className="mono">
                  {POC.appUrl.replace("https://", "")}
                </a>{" "}
                <Copy text={POC.appUrl} />
              </dd>
              <dt>Tenant API (dev)</dt>
              <dd className="mono">
                {POC.edgeUrl.replace("https://", "")} <Copy text={POC.edgeUrl} />
                <br />
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  with header <code>X-AC-Tenant-Host: {POC.primaryHost}</code>
                </span>
              </dd>
              <dt>Primary host (prod)</dt>
              <dd className="mono">
                {POC.primaryHost}{" "}
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  — custom-domain identity, live once actionconnect.com routes land
                </span>
              </dd>
              <dt>Inbound email</dt>
              <dd className="mono">{POC.inboundEmail}</dd>
              <dt>Tenant D1</dt>
              <dd className="mono">{POC.d1Name}<br />{POC.d1Id}</dd>
            </dl>

            <h3 style={{ marginTop: 18 }}>Admin login (agent app)</h3>
            <dl className="kv">
              <dt>Email</dt>
              <dd className="mono">{POC.adminEmail} <Copy text={POC.adminEmail} /></dd>
              <dt>Password</dt>
              <dd className="mono">{POC.adminPassword} <Copy text={POC.adminPassword} /></dd>
              <dt>Role</dt>
              <dd>Administrator (full access)</dd>
            </dl>
          </div>
        </div>

        {/* ---- How dev access works ---- */}
        <div className="card">
          <div className="card-head">
            <h2>How to reach it on staging (dev URLs)</h2>
          </div>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>
              Production will serve this tenant on its own custom domain (<code>{POC.primaryHost}</code>), which
              isn&apos;t routed yet. On staging we don&apos;t need it: the app and edge run on Cloudflare dev
              domains and the edge resolves the tenant from an <code>X-AC-Tenant-Host</code> header instead of the
              URL host. Built into the staging builds — nothing to do per request.
            </p>
            <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
              <li>
                <strong>Open the app</strong> at{" "}
                <a href={POC.appUrl} target="_blank" rel="noreferrer" className="mono">
                  {POC.appUrl.replace("https://", "")}
                </a>{" "}
                and sign in with the POC admin below. (Built with{" "}
                <code>vite build --mode staging</code>, which sets <code>VITE_API_BASE</code> to the staging edge
                and <code>VITE_TENANT_HOST={POC.primaryHost}</code>.)
              </li>
              <li>
                <strong>Impersonate</strong> from a tenant&apos;s detail page — it opens the staging edge&apos;s
                redeem endpoint, which redirects back to the staging app (<code>APP_BASE_URL</code> on ac-edge).
              </li>
              <li>
                <strong>One-time:</strong> set <code>CF_ACCOUNT_ID</code> / <code>CF_API_TOKEN</code> secrets on
                ac-edge-staging so it can reach the tenant D1 over the HTTP query API, and{" "}
                <code>ALLOW_HOST_OVERRIDE=1</code> (already in <code>wrangler.toml</code>) so the header is honored
                — see <Link to="/resources/api-keys">API keys &amp; vendors</Link>.
              </li>
            </ol>
            <p className="muted" style={{ fontSize: "0.84rem", marginBottom: 0 }}>
              Direct API smoke test (no browser):{" "}
              <code>
                curl -H &quot;X-AC-Tenant-Host: {POC.primaryHost}&quot; {POC.edgeUrl}/api/auth/login
                -d &apos;&#123;&quot;email&quot;:&quot;{POC.adminEmail}&quot;,&quot;password&quot;:&quot;{POC.adminPassword}&quot;&#125;&apos;
              </code>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
